import { type ClusterRef, sortCallingCodes } from "@seagames/contracts";
import { Select, type SelectOption } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { clusterOptionsOptions, providerOptionsOptions } from "../../lib/api/transport-fleet";
import { countryOptionsOptions } from "../../lib/countries/queries";

/**
 * Calling codes without the `+`, from the countries table (`/countries/options`,
 * cached 30 min, any signed-in role) — the phone pickers and `formatPhone`.
 * Southeast Asia first (`sortCallingCodes`), then numeric.
 */
export function useCallingCodes(): string[] {
	const countries = useQuery(countryOptionsOptions());
	return useMemo(
		() =>
			sortCallingCodes([...new Set((countries.data ?? []).map((c) => c.calling_code?.replace(/^\+/, "")).filter((c): c is string => !!c))]),
		[countries.data],
	);
}

/** "KL · Kuala Lumpur". */
export const clusterLabel = (c: Pick<ClusterRef, "code" | "name">): string => `${c.code} · ${c.name}`;

/**
 * A cluster in a table cell: its code as a small navy tag, the full name in
 * the tooltip and for screen readers. Compact on purpose — the Venues and
 * Entry points tables must still fit at 1440 px with this column (E2E B2);
 * the codes are the ones the Clusters sheet lists.
 */
export function ClusterCell({ cluster }: { cluster: Pick<ClusterRef, "code" | "name"> }) {
	return (
		<span className="inline-flex h-[1.25rem] items-center rounded-[0.25rem] bg-gms-navy-soft px-[0.375rem] text-[0.65625rem] font-bold tracking-[0.06em] whitespace-nowrap text-gms-navy" title={clusterLabel(cluster)}>
			{cluster.code}
			<span className="sr-only"> {cluster.name}</span>
		</span>
	);
}

/**
 * The cluster picker (fleet sheets, places, and the Venues / Entry points
 * settings forms): `/clusters/options`, open to any signed-in GMS role. A
 * cluster the row already points at stays pickable even when it is not in
 * the options any more, so saving never silently drops it.
 */
export function ClusterSelect({
	id,
	value,
	onChange,
	current,
	clearable = false,
	disabled,
	size = "md",
}: {
	id?: string;
	value: string;
	onChange: (clusterId: string) => void;
	/** The row's stored cluster, kept pickable. */
	current?: ClusterRef | null;
	clearable?: boolean;
	disabled?: boolean;
	size?: "sm" | "md";
}) {
	const { t } = useTranslation("transportFleet");
	const clusters = useQuery(clusterOptionsOptions());
	const options = useMemo<SelectOption[]>(() => {
		const list = (clusters.data ?? []).map((c) => ({ value: c.cluster_id, label: clusterLabel(c) }));
		return current && !list.some((o) => o.value === current.cluster_id) ? [{ value: current.cluster_id, label: clusterLabel(current) }, ...list] : list;
	}, [clusters.data, current]);
	return (
		<Select
			id={id}
			size={size}
			value={value || null}
			onChange={(v) => onChange(v ?? "")}
			options={options}
			placeholder={clusters.isPending ? t("common.loading") : t("cluster.choose")}
			clearable={clearable}
			disabled={disabled}
		/>
	);
}

/** The transport companies for a picker; `null` = the host's own fleet. */
export function useProviderSelectOptions(): { options: SelectOption[]; loading: boolean } {
	const providers = useQuery(providerOptionsOptions());
	return {
		options: (providers.data ?? []).map((p) => ({ value: p.transport_provider_id, label: p.name })),
		loading: providers.isPending,
	};
}
